/**
 * Formats a category-scoped specials page title. Category labels stored in
 * Dutchie are typically plural ("Concentrates", "Edibles", "Pre-Rolls"),
 * which reads awkwardly next to "Specials" — "Concentrates Specials". The
 * page is showing specials *within* a category, so we singularize the label
 * so it reads "Concentrate Specials", "Edible Specials", etc.
 *
 * Examples:
 *   Flower         → "Flower Specials"
 *   Concentrates   → "Concentrate Specials"
 *   Edibles        → "Edible Specials"
 *   Pre-Rolls      → "Pre-Roll Specials"
 *   Accessories    → "Accessory Specials"
 *   Apparel        → "Apparel Specials"
 *
 * When `hideSalesLanguage` is true (e.g. MA regulations), "Specials" becomes
 * "Offers" and the same singularization applies.
 */
export function formatCategorySpecialsTitle(categoryLabel: string, hideSalesLanguage: boolean): string {
  const suffix = hideSalesLanguage ? 'Offers' : 'Specials';
  return `${singularizeCategoryLabel(categoryLabel)} ${suffix}`;
}

function singularizeCategoryLabel(label: string): string {
  // "Accessories" → "Accessory"
  if (/[^aeiou]ies$/i.test(label)) {
    return `${label.slice(0, -3)}y`;
  }
  // Keep words ending in "ss" (e.g. "Glass") and anything not ending in a
  // lowercase "s" as-is. Uppercase trailing "S" (acronyms like "CBD") stays.
  if (/[^s]s$/.test(label)) {
    return label.slice(0, -1);
  }
  return label;
}
